import { Tool, Shape } from '@hcie/core';
import { tooltipManager, EventBus, ToolContext, VectorSelection } from '@hcie/shared';

declare global {
  interface Window {
    vectorToolManager: any;
    vectorSelection: VectorSelection | null;
  }
}

export class VectorToolManager {
    public isDrawing: boolean = false;
    public isEditing: boolean = false;
    public startX: number = 0;
    public startY: number = 0;
    public currentShape: Shape | null = null;
    public activeLayerIndex: number = -1;
    public selectedShapeIndex: number = -1;

    public overlay: HTMLDivElement | null = null;
    public controls: HTMLDivElement | null = null;
    public dragStartX: number = 0;
    public dragStartY: number = 0;
    public editStartX: number = 0;
    public editStartY: number = 0;
    public dragStartLeft: number = 0;
    public dragStartTop: number = 0;

    public isDraggingOverlay: boolean = false;

    private originalShape: Shape | null = null;
    public isResizing: boolean = false;
    private resizeDir: string = '';
    private resizeStartX: number = 0;
    private resizeStartY: number = 0;
    private resizeStartShape: Shape | null = null;
    private handles: Record<string, HTMLDivElement> = {};

    public isRotating: boolean = false;
    private rotateStartX: number = 0;
    private rotateStartY: number = 0;
    private rotateStartAngle: number = 0;

    private dragStartShapeX1: number = 0;
    private dragStartShapeY1: number = 0;
    private dragStartShapeX2: number = 0;
    private dragStartShapeY2: number = 0;

    constructor() {
        this.init();
    }

    private getLocalCoords(ctx: ToolContext, e: MouseEvent): { x: number, y: number } {
        const can = document.getElementById('drawingCanvas') as HTMLCanvasElement;
        if (!can) return { x: 0, y: 0 };
        const rect = can.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) / ctx.zoom,
            y: (e.clientY - rect.top) / ctx.zoom
        };
    }

    init() {
        // Shared logic: instead of individual listeners, we'll respond to ToolContext events
        // Property change listeners via EventBus
        EventBus.on('TOOL_CANCEL_REQUESTED', () => {
            if (this.isEditing) this.cancelEdit();
        });

        // Listen for property changes in Properties panel
        window.addEventListener('fillChanged', (e: any) => {
            if (this.isEditing && this.currentShape) {
                this.currentShape.style.fill = e.detail.value;
                EventBus.emit('RENDER_LAYERS');
            }
        });
        window.addEventListener('penWidthChanged', (e: any) => {
            if (this.isEditing && this.currentShape) {
                this.currentShape.style.width = e.detail.value;
                EventBus.emit('RENDER_LAYERS');
            }
        });
        window.addEventListener('syncOpacity', (e: any) => {
            if (this.isEditing && this.currentShape) {
                this.currentShape.style.opacity = parseFloat(e.detail.value) / 100;
                EventBus.emit('RENDER_LAYERS');
            }
        });
        window.addEventListener('hardnessChanged', (e: any) => {
            if (this.isEditing && this.currentShape) {
                this.currentShape.style.hardness = e.detail.value;
                EventBus.emit('RENDER_LAYERS');
            }
        });
        window.addEventListener('cornerRadiusChanged', (e: any) => {
            if (this.isEditing && this.currentShape) {
                this.currentShape.style.cornerRadius = e.detail.value;
                EventBus.emit('RENDER_LAYERS');
            }
        });

        // Mapping legacy window events to EventBus property changes (or vice versa temporarily)
        this.createOverlayUI();
    }

    public handleMouseDown(ctx: ToolContext, e: MouseEvent, x: number, y: number) {
        console.log(`[DEBUG 2026-04-05][TaskID: #1324] VTM.handleMouseDown: x=${x.toFixed(1)}, y=${y.toFixed(1)}, toolId=${ctx.g.current_tool.id}`);
        
        // If a handle interaction is already in progress, skip canvas mousedown
        if (this.isDraggingOverlay || this.isResizing || this.isRotating) return;
        
        const activeLayer = ctx.layers[ctx.activeLayerIndex];
        if (!activeLayer) {
            console.error("[DEBUG 1324][VTM] No active layer during MouseDown.");
            return;
        }

        if (ctx.g.current_tool.id === Tool.VectorSelect.id) {
            if (this.isEditing) return;
            this.trySelectShapeAt(ctx, x, y);
            return;
        }

        if (this.isEditing) return;
        // if (activeLayer.type === 'raster') return; // REMOVED: Allow ghost drawing on raster


        this.isDrawing = true;
        this.startX = x;
        this.startY = y;
        this.activeLayerIndex = ctx.activeLayerIndex;

        this.currentShape = {
            id: 'v-' + Date.now(),
            type: this.getShapeTypeFromTool(ctx.g.current_tool.id) as any,
            x1: x, y1: y, x2: x, y2: y,
            style: {
                color: ctx.g.pen_color,
                width: ctx.g.pen_width,
                opacity: ctx.g.pen_opacity,
                cap: ctx.g.pen_cap,
                hardness: ctx.g.brush_hardness,
                cornerRadius: ctx.g.round_rect_corner_radius,
                fill: !!ctx.g.shape_fill,
                fillColor: ctx.g.pen_secondary_color
            }
        };
    }

    public handleMouseMove(ctx: ToolContext, e: MouseEvent, x: number, y: number) {
        if (this.isDrawing) {
             console.log(`[DEBUG 2026-04-05][TaskID: #1324] VTM.handleMouseMove (drawing): x=${x.toFixed(1)}, y=${y.toFixed(1)}`);
        }
        if (ctx.g.current_tool.id === Tool.VectorSelect.id && !this.isEditing && !this.isDrawing) {
            this.updateCursor(ctx, x, y);
        }

        if (this.isDraggingOverlay && this.currentShape) {
            const dx = (e.clientX - this.dragStartX) / ctx.zoom;
            const dy = (e.clientY - this.dragStartY) / ctx.zoom;
            this.currentShape.x1 = this.dragStartShapeX1 + dx;
            this.currentShape.y1 = this.dragStartShapeY1 + dy;
            this.currentShape.x2 = this.dragStartShapeX2 + dx;
            this.currentShape.y2 = this.dragStartShapeY2 + dy;
            
            if (this.controls) this.controls.style.display = 'none';

            this.updateOverlay(ctx, this.currentShape);
            this.updateStatusBarInfo(this.currentShape);
            EventBus.emit('RENDER_LAYERS');
            return;
        }

        if (this.isResizing && this.currentShape && this.resizeStartShape) {
            const dx = (e.clientX - this.resizeStartX) / ctx.zoom;
            const dy = (e.clientY - this.resizeStartY) / ctx.zoom;
            const s = this.resizeStartShape;

            if (this.resizeDir.includes('e')) this.currentShape.x2 = s.x2 + dx;
            if (this.resizeDir.includes('w')) this.currentShape.x1 = s.x1 + dx;
            if (this.resizeDir.includes('s')) this.currentShape.y2 = s.y2 + dy;
            if (this.resizeDir.includes('n')) this.currentShape.y1 = s.y1 + dy;

            if (this.controls) this.controls.style.display = 'none';

            this.updateOverlay(ctx, this.currentShape);
            this.updateStatusBarInfo(this.currentShape);
            EventBus.emit('RENDER_LAYERS');
            return;
        }

        if (this.isRotating && this.currentShape) {
            const s = this.currentShape;
            let cx = (Math.min(s.x1, s.x2) + Math.max(s.x1, s.x2)) / 2;
            let cy = (Math.min(s.y1, s.y2) + Math.max(s.y1, s.y2)) / 2;
            if (s.type === 'circle') { cx = s.x1; cy = s.y1; }
            
            const can = document.getElementById('drawingCanvas');
            const rect = can?.getBoundingClientRect();
            if (rect) {
                const screenCx = rect.left + cx * ctx.zoom;
                const screenCy = rect.top + cy * ctx.zoom;
                
                const startAngle = Math.atan2(this.rotateStartY - screenCy, this.rotateStartX - screenCx) * 180 / Math.PI;
                const currentAngle = Math.atan2(e.clientY - screenCy, e.clientX - screenCx) * 180 / Math.PI;
                
                let diff = currentAngle - startAngle;
                this.currentShape.style.angle = (this.rotateStartAngle + diff) % 360;
                
                if (this.controls) this.controls.style.display = 'none';
                
                this.updateOverlay(ctx, this.currentShape);
                this.updateStatusBarInfo(this.currentShape);
                EventBus.emit('RENDER_LAYERS');
            }
            return;
        }

        if (!this.isDrawing || !this.currentShape) return;

        let finalX = x;
        let finalY = y;

        // 1324-4: Line Angle Snap - Optimized for 0, 30, 45, 60, 90 and their multiples
        if (ctx.g.current_tool.id === Tool.Line.id && e.shiftKey) {
            const dx = x - this.startX;
            const dy = y - this.startY;
            const dist = Math.hypot(dx, dy);
            if (dist > 0) {
                const angle = Math.atan2(dy, dx) * 180 / Math.PI;
                const snapAngles = [0, 30, 45, 60, 90, 120, 135, 150, 180, -30, -45, -60, -90, -120, -135, -150];
                let closest = snapAngles[0];
                let minDiff = Infinity;
                for (const snap of snapAngles) {
                    const diff = Math.abs(angle - snap);
                    const diff360 = Math.abs(angle - snap + 360) % 360;
                    const finalDiff = Math.min(diff, diff360);
                    if (finalDiff < minDiff) { 
                        minDiff = finalDiff; 
                        closest = snap; 
                    }
                }
                const snappedRad = closest * Math.PI / 180;
                finalX = this.startX + Math.cos(snappedRad) * dist;
                finalY = this.startY + Math.sin(snappedRad) * dist;
            }
        }

        this.currentShape.x2 = finalX;
        this.currentShape.y2 = finalY;

        // 1324-2 & 3: Status Bar Updates
        this.updateStatusBarInfo(this.currentShape);

        console.log(`[DEBUG 2026-04-05][TaskID: #1324] VTM.handleMouseMove: x2=${finalX.toFixed(1)}, y2=${finalY.toFixed(1)}`);
        EventBus.emit('RENDER_LAYERS');

    }

    public handleMouseUp(ctx: ToolContext, _e: MouseEvent) {
        console.log(`[DEBUG 2026-04-05][TaskID: #1324] VTM.handleMouseUp: isDrawing=${this.isDrawing}, isEditing=${this.isEditing}`);
        if (this.isDraggingOverlay || this.isResizing || this.isRotating) {
            this.isDraggingOverlay = false;
            this.isResizing = false;
            this.isRotating = false;
            if (this.controls) this.controls.style.display = 'flex';
            return;
        }

        if (!this.isDrawing || !this.currentShape) return;
        this.isDrawing = false;

        const layer = ctx.layers[this.activeLayerIndex];
        if (layer) {
            // Unify workflow: enter edit mode for both raster and vector layers
            console.log(`[DEBUG 2026-04-05][TaskID: #1324] VTM.handleMouseUp: Entering Edit/Ghost mode.`);
            this.isEditing = true;
            
            // If it's a vector layer and we just finished the first stroke, we should keep it selected
            if (layer.type === 'vector') {
                if (!layer.shapes) layer.shapes = [];
                layer.shapes.push(this.currentShape);
                this.selectedShapeIndex = layer.shapes.length - 1;
            }

            this.updateOverlay(ctx, this.currentShape);
            if (this.overlay) this.overlay.style.display = 'block';
            if (this.controls) this.controls.style.display = 'flex';
            this.notifyVectorSelectionChanged(true, this.currentShape.type, this.currentShape.style);
            
            // Force sync to status bar and properties panel
            this.updateStatusBarInfo(this.currentShape);
        }

        EventBus.emit('RENDER_LAYERS');
    }

    private isVectorTool(toolId: string): boolean {
        return [
            Tool.Line.id, 
            Tool.Rectangle.id, 
            Tool.Circle.id, 
            Tool.Ellipse.id, 
            Tool.Rounded_Rectangle.id
        ].includes(toolId);
    }

    public handlePropertyChange(ctx: ToolContext, prop: string, value: any) {
        if (!this.isEditing) return;
        
        // Get the shape to modify: either the layer shape or the ghost currentShape
        let shape: Shape | null = null;
        if (this.selectedShapeIndex !== -1) {
            const layer = ctx.layers[ctx.activeLayerIndex];
            if (layer && layer.shapes) {
                shape = layer.shapes[this.selectedShapeIndex];
            }
        }
        // Fallback to currentShape for ghost mode (raster layers)
        if (!shape && this.currentShape) {
            shape = this.currentShape;
        }
        if (!shape) return;

        if (prop === 'fillColor') {
            shape.style.fillColor = value;
        } else if (prop === 'fill') {
            shape.style.fill = value;
        } else if (['x1', 'y1', 'x2', 'y2'].includes(prop)) {
            (shape as any)[prop] = value;
            this.updateOverlay(ctx, shape);
        } else if (prop === 'angle') {
            shape.style.angle = value;
            this.updateOverlay(ctx, shape);
        } else {
            (shape.style as any)[prop] = value;
        }
        this.updateStatusBarInfo(shape);
        EventBus.emit('RENDER_LAYERS');
    }

    public updateShapeGeometry(ctx: ToolContext, geometry: Partial<Shape & { angle: number, radius: number }>) {
        if (!this.isEditing || !this.currentShape) return;
        
        if (geometry.x1 !== undefined) this.currentShape.x1 = geometry.x1;
        if (geometry.y1 !== undefined) this.currentShape.y1 = geometry.y1;
        if (geometry.x2 !== undefined) this.currentShape.x2 = geometry.x2;
        if (geometry.y2 !== undefined) this.currentShape.y2 = geometry.y2;
        
        if ((geometry as any).radius !== undefined && this.currentShape.type === 'circle') {
            // For circles, radius changes x2/y2 relative to center (x1,y1)
            this.currentShape.x2 = this.currentShape.x1 + (geometry as any).radius;
            this.currentShape.y2 = this.currentShape.y1;
        }
        
        if (geometry.angle !== undefined) {
            if (this.currentShape.type === 'line') {
                // For lines, convert angle back to x2/y2 
                const dist = Math.hypot(this.currentShape.x2 - this.currentShape.x1, this.currentShape.y2 - this.currentShape.y1);
                const rad = geometry.angle * Math.PI / 180;
                this.currentShape.x2 = this.currentShape.x1 + dist * Math.cos(rad);
                this.currentShape.y2 = this.currentShape.y1 + dist * Math.sin(rad);
            } else {
                this.currentShape.style.angle = geometry.angle;
            }
        }

        this.updateOverlay(ctx, this.currentShape);
        this.updateStatusBarInfo(this.currentShape);
        EventBus.emit('RENDER_LAYERS');
    }

    private createOverlayUI() {
        if (typeof document === 'undefined') return;
        this.overlay = document.createElement('div');
        this.overlay.id = 'vector-edit-overlay';
        this.overlay.style.cssText = `
            position: absolute;
            border: 1px dashed #007bff;
            display: none;
            z-index: 1000;
            box-sizing: border-box;
            background: rgba(0, 123, 255, 0.05);
            pointer-events: none;
        `;

        this.controls = document.createElement('div');
        this.controls.id = 'vector-edit-controls';
        this.controls.style.cssText = `
            position: absolute;
            display: none;
            gap: 8px;
            padding: 5px;
            background: #222;
            border-radius: 4px;
            border: 1px solid #444;
            pointer-events: auto;
        `;

        const moveHandle = document.createElement('div');
        moveHandle.innerHTML = 'Position';
        moveHandle.style.cssText = 'color: #ccc; font-size: 11px; cursor: move; padding: 2px 5px; user-select: none;';
        moveHandle.onmousedown = (e) => this.onOverlayDragStart(e);

        const okBtn = document.createElement('button');
        okBtn.innerText = 'OK';
        okBtn.style.cssText = 'background: #28a745; color: white; border: none; padding: 2px 8px; border-radius: 2px; cursor: pointer; font-size: 11px; font-weight: bold;';
        okBtn.onclick = (e) => { e.stopPropagation(); this.commitEdit(); };

        const cancelBtn = document.createElement('button');
        cancelBtn.innerText = 'Cancel';
        cancelBtn.style.cssText = 'background: #dc3545; color: white; border: none; padding: 2px 8px; border-radius: 2px; cursor: pointer; font-size: 11px; font-weight: bold;';
        cancelBtn.onclick = (e) => { e.stopPropagation(); this.cancelEdit(); };

        this.controls.appendChild(moveHandle);
        this.controls.appendChild(okBtn);
        this.controls.appendChild(cancelBtn);

        // Resize handles
        const handles = ['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se'];
        handles.forEach(dir => {
            const h = document.createElement('div');
            h.style.cssText = `
                position: absolute; width: 8px; height: 8px; background: white;
                border: 1px solid #007bff; pointer-events: auto;
            `;
            if (dir.includes('n')) h.style.top = '-4px';
            if (dir.includes('s')) h.style.bottom = '-4px';
            if (dir.includes('e')) h.style.right = '-4px';
            if (dir.includes('w')) h.style.left = '-4px';
            if (dir === 'n' || dir === 's') h.style.left = '50%';
            if (dir === 'e' || dir === 'w') h.style.top = '50%';
            if (dir === 'n' || dir === 's') h.style.marginLeft = '-4px';
            if (dir === 'e' || dir === 'w') h.style.marginTop = '-4px';

            h.style.cursor = `${dir}-resize`;
            h.onmousedown = (e) => this.onResizeStart(e, dir);
            if (this.overlay) this.overlay.appendChild(h);
            this.handles[dir] = h;
        });

        // Rotate handles
        const rotHandles = ['rot-nw', 'rot-ne', 'rot-sw', 'rot-se'];
        rotHandles.forEach(dir => {
            const h = document.createElement('div');
            h.style.cssText = `
                position: absolute; width: 14px; height: 14px; background: rgba(0,0,0,0);
                border-radius: 50%; pointer-events: auto; cursor: alias;
            `;
            if (dir.includes('n')) h.style.top = '-15px';
            if (dir.includes('s')) h.style.bottom = '-15px';
            if (dir.includes('e')) h.style.right = '-15px';
            if (dir.includes('w')) h.style.left = '-15px';
            
            h.onmousedown = (e) => this.onRotateStart(e);
            if (this.overlay) this.overlay.appendChild(h);
            this.handles[dir] = h;
        });

        const wrapper = document.getElementById('canvasWrapper');
        if (wrapper) {
            wrapper.appendChild(this.overlay);
            wrapper.appendChild(this.controls);
            this.controls.style.zIndex = '1005';
        }
    }

    public showWarning(show: boolean) {
        if (show) {
            tooltipManager.showWarning("⚠️ Vector Tool on Raster Layer");
        }
    }

    private onOverlayDragStart(e: MouseEvent) {
        e.stopPropagation();
        e.preventDefault();
        this.isDraggingOverlay = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        if (this.currentShape) {
            this.dragStartShapeX1 = this.currentShape.x1;
            this.dragStartShapeY1 = this.currentShape.y1;
            this.dragStartShapeX2 = this.currentShape.x2;
            this.dragStartShapeY2 = this.currentShape.y2;
        }
    }

    private onResizeStart(e: MouseEvent, dir: string) {
        e.stopPropagation();
        e.preventDefault();
        this.isResizing = true;
        this.resizeDir = dir;
        this.resizeStartX = e.clientX;
        this.resizeStartY = e.clientY;
        if (this.currentShape) {
            this.resizeStartShape = JSON.parse(JSON.stringify(this.currentShape));
        }
    }

    private onRotateStart(e: MouseEvent) {
        e.stopPropagation();
        e.preventDefault();
        this.isRotating = true;
        this.rotateStartX = e.clientX;
        this.rotateStartY = e.clientY;
        if (this.currentShape) {
            this.rotateStartAngle = this.currentShape.style.angle || 0;
        }
    }

    private updateCursor(ctx: ToolContext, x: number, y: number) {
        const layer = ctx.layers[ctx.activeLayerIndex];
        const can = document.getElementById('drawingCanvas');
        if (!can || !layer || !layer.shapes) {
            if (can) can.style.cursor = 'crosshair';
            return;
        }

        let found = false;
        for (let i = layer.shapes.length - 1; i >= 0; i--) {
            if (this.isPointInShape(layer.shapes[i], x, y)) {
                found = true;
                break;
            }
        }
        can.style.cursor = found ? 'pointer' : 'crosshair';
    }

    private getShapeTypeFromTool(toolId: string): string {
        if (toolId === Tool.Line.id) return 'line';
        if (toolId === Tool.Circle.id) return 'circle';
        if (toolId === Tool.Rectangle.id) return 'rect';
        if (toolId === Tool.Rounded_Rectangle.id) return 'roundrect';
        if (toolId === Tool.Ellipse.id) return 'ellipse';
        return 'rect';
    }

    public trySelectShapeAt(ctx: ToolContext, x: number, y: number) {
        // [TaskID: #1324] First check if we're clicking a text layer across all layers
        for (let i = ctx.layers.length - 1; i >= 0; i--) {
            const l = ctx.layers[i];
            if (l.visible && l.type === 'text') {
                if ((window as any).textTool && (window as any).textTool.hitTest(l, x, y)) {
                    EventBus.emit('SELECT_LAYER', { index: i });
                    EventBus.emit('TOOL_CHANGED', { toolId: 'btn-text' });
                    (window as any).textTool.startEditing(x, y, i);
                    return;
                }
            }
        }

        const layer = ctx.layers[ctx.activeLayerIndex];
        if (!layer || !layer.shapes) return;

        for (let i = layer.shapes.length - 1; i >= 0; i--) {
            if (this.isPointInShape(layer.shapes[i], x, y)) {
                console.log(`[DEBUG 2026-04-05][TaskID: #1324] VTM.trySelectShapeAt: Found shape at index ${i}`);
                this.startEdit(ctx, i);
                return;
            }
        }
        console.log(`[DEBUG 2026-04-05][TaskID: #1324] VTM.trySelectShapeAt: No shape found at ${x}, ${y}`);

    }

    private isPointInShape(shape: Shape, x: number, y: number): boolean {
        let px = x;
        let py = y;
        
        if (shape.style.angle && shape.type !== 'line') {
             let cx = (Math.min(shape.x1, shape.x2) + Math.max(shape.x1, shape.x2)) / 2;
             let cy = (Math.min(shape.y1, shape.y2) + Math.max(shape.y1, shape.y2)) / 2;
             if (shape.type === 'circle') { cx = shape.x1; cy = shape.y1; }
             
             const angleRad = -shape.style.angle * Math.PI / 180;
             const dx = x - cx;
             const dy = y - cy;
             px = cx + dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
             py = cy + dx * Math.sin(angleRad) + dy * Math.cos(angleRad);
        }

        const margin = 8;
        if (shape.type === 'line') {
            // Lines use original x,y since they don't have bounding box rotation
            const L2 = Math.pow(shape.x2 - shape.x1, 2) + Math.pow(shape.y2 - shape.y1, 2);
            if (L2 === 0) return Math.sqrt(Math.pow(x - shape.x1, 2) + Math.pow(y - shape.y1, 2)) < margin;
            let t = ((x - shape.x1) * (shape.x2 - shape.x1) + (y - shape.y1) * (shape.y2 - shape.y1)) / L2;
            t = Math.max(0, Math.min(1, t));
            const dist = Math.sqrt(Math.pow(x - (shape.x1 + t * (shape.x2 - shape.x1)), 2) + Math.pow(y - (shape.y1 + t * (shape.y2 - shape.y1)), 2));
            return dist < margin + (shape.style.width / 2);
        }
        if (shape.type === 'circle') {
            const r = Math.sqrt(Math.pow(shape.x2 - shape.x1, 2) + Math.pow(shape.y2 - shape.y1, 2));
            const dist = Math.sqrt(Math.pow(px - shape.x1, 2) + Math.pow(py - shape.y1, 2));
            return dist <= r + margin;
        }
        if (shape.type === 'ellipse') {
            const rx = Math.abs(shape.x2 - shape.x1) / 2;
            const ry = Math.abs(shape.y2 - shape.y1) / 2;
            const cx = (shape.x1 + shape.x2) / 2;
            const cy = (shape.y1 + shape.y2) / 2;
            if (rx === 0 || ry === 0) return false;
            const val = Math.pow(px - cx, 2) / Math.pow(rx + margin, 2) + Math.pow(py - cy, 2) / Math.pow(ry + margin, 2);
            return val <= 1;
        }
        const left = Math.min(shape.x1, shape.x2) - margin;
        const right = Math.max(shape.x1, shape.x2) + margin;
        const top = Math.min(shape.y1, shape.y2) - margin;
        const bottom = Math.max(shape.y1, shape.y2) + margin;
        return px >= left && px <= right && py >= top && py <= bottom;
    }

    public startEdit(ctx: ToolContext, index: number) {
        const layer = ctx.layers[ctx.activeLayerIndex];
        if (!layer || !layer.shapes || !layer.shapes[index]) return;

        this.isEditing = true;
        this.selectedShapeIndex = index;
        this.currentShape = layer.shapes[index];
        this.originalShape = JSON.parse(JSON.stringify(this.currentShape));
        layer.isBeingEdited = true;

        this.updateOverlay(ctx, this.currentShape as Shape);
        if (this.overlay) this.overlay.style.display = 'block';
        if (this.controls) this.controls.style.display = 'flex';
        this.notifyVectorSelectionChanged(true, this.currentShape?.type, this.currentShape?.style);
        
        // Force UI sync
        if (this.currentShape) {
            this.updateStatusBarInfo(this.currentShape as Shape);
        }

        EventBus.emit('RENDER_LAYERS');
    }

    public updateOverlay(ctx: ToolContext, shape: Shape) {
        if (!this.overlay) return;
        let left, top, w, h;
        if (shape.type === 'circle') {
            const r = Math.sqrt(Math.pow(shape.x2 - shape.x1, 2) + Math.pow(shape.y2 - shape.y1, 2));
            left = shape.x1 - r; top = shape.y1 - r; w = r * 2; h = r * 2;
        } else {
            left = Math.min(shape.x1, shape.x2); top = Math.min(shape.y1, shape.y2);
            w = Math.abs(shape.x2 - shape.x1); h = Math.abs(shape.y2 - shape.y1);
        }
        this.overlay.style.left = (left * ctx.zoom) + 'px';
        this.overlay.style.top = (top * ctx.zoom) + 'px';
        this.overlay.style.width = (w * ctx.zoom) + 'px';
        this.overlay.style.height = (h * ctx.zoom) + 'px';
        
        if (shape.style.angle) {
            this.overlay.style.transform = `rotate(${shape.style.angle}deg)`;
            this.overlay.style.transformOrigin = 'center';
        } else {
            this.overlay.style.transform = 'none';
        }

        if (this.controls) {
            // Stable positioning: above the shape's center to avoid jittering
            const centerX = (shape.x1 + shape.x2) / 2;
            const topY = Math.min(shape.y1, shape.y2);
            
            this.controls.style.left = (centerX * ctx.zoom - 60) + 'px'; 
            this.controls.style.top = (topY * ctx.zoom - 45) + 'px';
            this.controls.style.transform = 'none';
        }
    }

    public commitEdit() {
        if (!this.isEditing || !this.currentShape) return;
        
        const shapeToCommit = this.currentShape;
        this.hideEditUI(); // This sets isEditing = false so globalAlpha is 1.0!
        
        EventBus.emit('VECTOR_COMMIT_REQUESTED', { shape: shapeToCommit });
    }

    private updateStatusBarInfo(s: Shape) {
        const msg = document.getElementById('statusMessage');
        const drawingInfo = document.getElementById('drawingInfo');
        if (!msg || !drawingInfo) return;
        
        drawingInfo.style.display = 'flex';
        msg.style.display = 'none'; // Hide generic message when detailed info is shown
        
        const w = Math.round(Math.abs(s.x2 - s.x1));
        const h = Math.round(Math.abs(s.y2 - s.y1));
        let angleValue = Math.round(s.style.angle || 0);
        if (s.type === 'line') {
            angleValue = Math.round(Math.atan2(s.y2 - s.y1, s.x2 - s.x1) * 180 / Math.PI);
        }
        
        const startXInput = document.getElementById('startXInput') as HTMLInputElement;
        const startYInput = document.getElementById('startYInput') as HTMLInputElement;
        const widthInput = document.getElementById('widthInput') as HTMLInputElement;
        const heightInput = document.getElementById('heightInput') as HTMLInputElement;
        const angleInput = document.getElementById('angleInput') as HTMLInputElement;
        const radiusInput = document.getElementById('radiusInput') as HTMLInputElement;
        
        const dimContainer = document.getElementById('dimContainer');
        const angleContainer = document.getElementById('angleContainer');
        const radiusContainer = document.getElementById('radiusContainer');

        if (startXInput) { startXInput.value = Math.round(s.x1).toString(); startXInput.disabled = false; }
        if (startYInput) { startYInput.value = Math.round(s.y1).toString(); startYInput.disabled = false; }
        
        if (dimContainer) dimContainer.style.display = (s.type === 'circle') ? 'none' : 'flex';
        if (radiusContainer) radiusContainer.style.display = (s.type === 'circle') ? 'flex' : 'none';
        if (angleContainer) angleContainer.style.display = (s.type === 'line' || s.type === 'rect' || s.type === 'ellipse' || s.type === 'roundrect') ? 'flex' : 'none';

        if (widthInput) { widthInput.value = w.toString(); widthInput.disabled = false; }
        if (heightInput) { heightInput.value = h.toString(); heightInput.disabled = false; }
        
        if (s.type === 'circle') {
            const r = Math.round(Math.hypot(s.x2 - s.x1, s.y2 - s.y1));
            if (radiusInput) { radiusInput.value = r.toString(); radiusInput.disabled = false; }
        } else if (s.type === 'line') {
            const dynamicAngle = Math.round(Math.atan2(s.y2 - s.y1, s.x2 - s.x1) * 180 / Math.PI);
            if (angleInput) { angleInput.value = dynamicAngle.toString(); angleInput.disabled = false; }
        } else {
            if (angleInput) { angleInput.value = angleValue.toString(); angleInput.disabled = false; }
        }
        
        // Also notify shared listeners for coordinate panel sync if active
        window.dispatchEvent(new CustomEvent('vectorShapeGeometryChanged', { detail: { 
            x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2, angle: angleValue, type: s.type
        }}));
    }


    public cancelEdit() {
        if (!this.isEditing) return;
        // Restoration logic moved to event-dispatcher or handled here if layers is still accessible
        // For now, assume this.currentShape was being modified in-place
        this.hideEditUI();
    }

    private hideEditUI() {
        this.isEditing = false;
        if (this.overlay) this.overlay.style.display = 'none';
        if (this.controls) this.controls.style.display = 'none';
        
        const msg = document.getElementById('statusMessage');
        const drawingInfo = document.getElementById('drawingInfo');
        if (msg) msg.style.display = 'inline';
        if (drawingInfo) drawingInfo.style.display = 'none';

        this.notifyVectorSelectionChanged(false);
        EventBus.emit('RENDER_LAYERS');
    }

    public deleteSelectedShape() {
        if (!this.isEditing) return;
        // Triggers via EventBus or direct if we have layer access
        this.hideEditUI();
        EventBus.emit('FINISH_DRAWING');
    }

    public notifyVectorSelectionChanged(hasSelection: boolean, shapeType: import('@hcie/shared').ShapeType | null = null, style: any = null) {
        window.vectorSelection = { hasSelection, shapeType: shapeType as any, style };
        window.dispatchEvent(new CustomEvent('vectorShapeSelectionChanged', { detail: { hasSelection, shapeType, style } }));
    }

    public drawShapes(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, shapes: Shape[]) {
        if (!shapes) return;
        shapes.forEach(s => {
            ctx.save();
            ctx.globalAlpha = (this.isEditing && s === this.currentShape) ? (s.style.opacity || 1.0) * 0.7 : (s.style.opacity || 1.0);
            this.drawSingleShape(ctx, s);
            ctx.restore();
        });
    }

    private drawSingleShape(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, s: Shape) {
        ctx.beginPath();
        ctx.strokeStyle = s.style.color;
        ctx.fillStyle = s.style.fillColor || 'transparent';
        ctx.lineWidth = s.style.width;
        ctx.lineCap = s.style.cap || 'round';
        ctx.lineJoin = 'round';
        
        // [TaskID: #1324] Soften vector edges based on hardness
        if (s.style.hardness !== undefined && s.style.hardness < 1.0) {
            const blurSize = (1.0 - s.style.hardness) * (s.style.width || 2);
            ctx.shadowBlur = blurSize;
            ctx.shadowColor = s.style.color;
        }
        
        let cx = (s.x1 + s.x2) / 2;
        let cy = (s.y1 + s.y2) / 2;
        if (s.type === 'circle') {
            cx = s.x1;
            cy = s.y1;
        } else if (s.type !== 'line') {
            cx = (Math.min(s.x1, s.x2) + Math.max(s.x1, s.x2)) / 2;
            cy = (Math.min(s.y1, s.y2) + Math.max(s.y1, s.y2)) / 2;
        }

        if (s.style.angle && s.type !== 'line') {
            ctx.translate(cx, cy);
            ctx.rotate(s.style.angle * Math.PI / 180);
            ctx.translate(-cx, -cy);
        }
        if (s.type === 'line') { ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); }
        else if (s.type === 'rect') { ctx.rect(s.x1, s.y1, s.x2 - s.x1, s.y2 - s.y1); }
        else if (s.type === 'circle') { const r = Math.sqrt(Math.pow(s.x2 - s.x1, 2) + Math.pow(s.y2 - s.y1, 2)); ctx.arc(s.x1, s.y1, r, 0, Math.PI * 2); }
        else if (s.type === 'ellipse') {
            const rx = Math.abs(s.x2 - s.x1) / 2; const ry = Math.abs(s.y2 - s.y1) / 2;
            const cx = (s.x1 + s.x2) / 2; const cy = (s.y1 + s.y2) / 2;
            ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        } else if (s.type === 'roundrect') {
            const x = Math.min(s.x1, s.x2); const y = Math.min(s.y1, s.y2);
            const w = Math.abs(s.x2 - s.x1); const h = Math.abs(s.y2 - s.y1);
            const r = s.style.cornerRadius || 10;
            if ((ctx as any).roundRect) (ctx as any).roundRect(x, y, w, h, r);
            else ctx.rect(x, y, w, h);
        }
        if (s.style.fill) ctx.fill();
        ctx.stroke();
    }
}

export const vectorToolManager = new VectorToolManager();
(window as any).vectorToolManager = vectorToolManager;

EventBus.on('APPLY_ZOOM', (payload) => {
    if (vectorToolManager.isEditing && vectorToolManager.currentShape) {
        // We need a context here, but APPLY_ZOOM only gives zoom. 
        // This suggests we still have some global state reliance or we need to pass a partial context.
        // For now, use a shim or rely on the overlay's own update logic if possible.
    }
});
